import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';

import { Box, Grid, Paper, Container, Typography } from '@material-ui/core';
import { SubscriptionsActions } from 'state/modules/subscriptions';
import useStyles from './PeriodsList.styled';
import { Table } from 'components/UI';
import moment from 'moment';
import { UsersSelect, VehiclesSelect } from 'components/AsyncFields';
import { ReservationStatusSelect } from 'components/DataFields';
import { DateTimePickerField } from 'components/FormFields';
import InputAdornment from '@material-ui/core/InputAdornment';
import TodayIcon from '@material-ui/icons/Today';
import { SearchField } from 'components/FormFields';
import { buildUrl, capitalize, toQueryString } from 'utils';
import { useQuery } from 'hooks';
import { useHistory } from 'react-router-dom';

const SubscriptionList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const urlParams = useQuery();
  const history = useHistory();

  const { fetchingSubscriptionsPeriods, periods, periodsPagination } = useSelector(
    (state) => state.subscriptions
  );
  const { localeFormat } = useSelector((state) => state.organizations);

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });

  const columns = [
    {
      name: i18n.t('tableColumns.id'),
      selector: 'id',
      sortable: true,
      grow: 0,
    },
    {
      name: i18n.t('tableColumns.customer'),
      selector: 'user_id',
      cell: (row) =>
        row?.user ? row?.user?.first_name + ' ' + (row?.user?.last_name || '') + ' ' : '--',
      sortable: true,
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.start_reserved'),
      selector: 'start_used',
      cell: (row) =>
        row.start_used && moment(row.start_used).isValid() ? (
          <DatetimeField datetime={row?.start_used} />
        ) : (
          '--'
        ),
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.end_reserved'),
      selector: 'end_used',
      cell: (row) =>
        row.end_used && moment(row.end_used).isValid() ? (
          <DatetimeField datetime={row?.end_used} />
        ) : (
          '--'
        ),
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.subscription'),
      selector: 'subscription',
      cell: (row) => row?.subscription?.display_name || '--',
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.vehicle'),
      selector: 'vehicle_id',
      cell: (row) => row?.vehicle?.display_name || '--',
      grow: 1,
      sortable: true,
    },
    {
      name: i18n.t('tableColumns.location'),
      selector: 'location',
      cell: (row) => row?.vehicle?.location?.name || '--',
      grow: 1,
      sortable: true,
    },
    {
      name: i18n.t('tableColumns.vehicle_group'),
      selector: 'vehicle_id',
      cell: (row) => row?.vehicle?.vehicle_group?.name || '--',
      grow: 1,
      sortable: true,
    },
    {
      name: i18n.t('tableColumns.invoice_status'),
      grow: 1,
      selector: (row) =>
        row?.status ? (
          <div className={`${classes.status} ${classes[row?.invoice?.status]}`}>
            <span className="status">
              {i18n.t(`invoice_statuses.${row?.invoice?.status}`, {
                defaultValue: capitalize(row?.invoice?.status?.toLowerCase() || '--'),
              })}
            </span>
          </div>
        ) : (
          '--'
        ),
    },
  ];

  const fetchSubscriptionsPeriods = useCallback(
    (params = {}) => {
      dispatch(SubscriptionsActions.fetchSubscriptionsPeriods(params));
    },
    [dispatch]
  );

  // Fetch on load/ params change
  useEffect(() => {
    fetchSubscriptionsPeriods(params);
  }, [fetchSubscriptionsPeriods, params]);

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  const handleParams = (params) => {
    const data = { ...params };
    // remove empty fields
    Object.keys(data).forEach((key) => !data[key] && delete data[key]);
    setParams(data);
    const qs = toQueryString(data);
    history.replace(buildUrl(`/subscriptions/reservation-periods${qs}`));
  };

  // sort filter
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    const _params = toQueryString(newParams);
    handleParams(_params);
  };

  // change number of items per page
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {};
  // search by status, keyword and organization
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      // validate if value is an object
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => !filters[key] && filters[key] !== false && delete filters[key]
    );
    handleParams({ ...filters });
  };

  const DatetimeField = ({ datetime, tz = moment.tz.guess() }) => {
    return (
      <span>
        <p>{moment(datetime).tz(tz).format(`${localeFormat} hh:mm A`)}</p>
        <p className={classes.timezone}>{moment().tz(tz).format('UTC Z')}</p>
      </span>
    );
  };

  return (
    <Container className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h6" gutterBottom>
                <Box fontWeight="fontWeightBold" data-cy="title" fontSize={24} my={1}>
                  {i18n.t('subscriptions.reservation_periods')}
                </Box>
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ paddingBottom: '25px' }}>
          <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="flex-end">
            <Grid item xs={12} sm={4} md={3}>
              <VehiclesSelect
                form={{
                  setFieldValue: search,
                }}
                field={{
                  name: 'vehicle_id',
                  value: params.vehicle_id || '',
                }}
                label={i18n.t('common.vehicle')}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <UsersSelect
                form={{
                  setFieldValue: search,
                }}
                type="users"
                margin="none"
                field={{
                  name: 'user_id',
                  value: params.user_id || '',
                }}
                label={i18n.t('common.filter_by_customer')}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <DateTimePickerField
                form={{
                  setFieldValue: search,
                }}
                field={{
                  name: 'start_used',
                  value: params.start_used || '',
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" style={{ color: '#8a8a8a' }}>
                      <TodayIcon />
                    </InputAdornment>
                  ),
                }}
                isClearable={true}
                editable={true}
                label={i18n.t('common.start_reserved')}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <DateTimePickerField
                form={{
                  setFieldValue: search,
                }}
                isClearable={true}
                field={{
                  name: 'end_used',
                  value: params.end_reserved || '',
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" style={{ color: '#8a8a8a' }}>
                      <TodayIcon />
                    </InputAdornment>
                  ),
                }}
                editable={true}
                label={i18n.t('common.end_reserved')}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <SearchField
                margin="none"
                label={`${i18n.t('subscriptions.subscription_id')}`}
                field={{
                  onChange: search,
                  value: params?.subscription_id || '',
                  name: 'subscription_id',
                }}
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <ReservationStatusSelect
                form={{
                  setFieldValue: search,
                }}
                margin="none"
                field={{
                  name: 'statuses',
                  value: params.statuses || '',
                }}
                label={i18n.t('common.status')}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Paper elevation={2} className={classes.paper}>
            <Table
              data={periods}
              columns={columns}
              loading={fetchingSubscriptionsPeriods}
              onSort={handleSort}
              handleRowClicked={handleRowClick}
              handlePageChange={handlePageChange}
              totalRows={periodsPagination?.totalRows || 0}
              handlePerRowsChange={handlePerRowsChange}
              page={params?.page}
              limit={params?.limit}
              defaultSortField={params?.sort_field}
              defaultSortAsc={!params?.sort_desc}
            />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SubscriptionList;
